import React from 'react'
import { breakpoints, Text, View } from 'oio-react'
import { Redirect, Route, Switch, useRouteMatch } from 'react-router-dom'
import { InboxGlyph16Icon } from 'assets/icons'
import { LAYOUT_BOX, LAYOUT_HEADER } from 'config/constants/ui'
import ListMenuButton from 'src/sites/kits/Global/components/ListMenuButton'
import Inbox from 'src/sites/kits/Messages/apps/Inbox'
import InitiativeThreadChat from 'src/sites/kits/Messages/apps/InitiativeThreadChat'
import UserThreadChat from 'src/sites/kits/Messages/apps/UserThreadChat'
import { LayoutContainerFixedArea, MultiPageContentPane, MultiPageNavPane, Title }
   from 'src/sites/kits/UI'
import NotFound from 'src/sites/kits/Utils/NotFound'
import { useInitiativeList, useMe, useMyDirectThreadList, useOrganization } from 'src/core/graphql/hooks'
import { withInitiativeHierarchyProvider } from 'src/sites/kits/Utils/InitiativeHierarchy'
import { useGlobalState } from 'src/sites/state'

// This is so that the Group Messages and Messages inside an initiative page
// can work the same (the InitiativeThreadChat assumes that an InitiativeHierarchy
// context provider exists)
const WrappedInitiativeThreadChat = withInitiativeHierarchyProvider({
   slugParam: 'initiativeSlug'
})(InitiativeThreadChat)

const Messages = () => {
   const match = useRouteMatch()
   const { myNotificationCount, theme } = useGlobalState()
   const initialSizeIsMobile = window.innerWidth < breakpoints.b
   const isBoxGL = theme.tmpGlobalLayout === LAYOUT_BOX
   const isHeaderGL = theme.tmpGlobalLayout === LAYOUT_HEADER

   const { myDirectThreadList } = useMyDirectThreadList()
   const { isLoggedIn, networkStatus } = useMe()
   const initialLoading = networkStatus <= 2

   const { organization } = useOrganization()
   const groupInitiativeTypeIds = organization.initiativeTypes
      .filter(t => t.enabled && t.class === 'group')
      .map(t => t.id)

   const { initiativeList } = useInitiativeList({
      levels: 'all',
      typeIds: groupInitiativeTypeIds,
      userRelationshipFilter: {
         userId: 'me',
         hasElementType: 'roleParticipant'
      }
   })

   let navPanePaddingLeft = `
      ${theme.tmpViewportPaddingA}[a]
      ${theme.tmpViewportPaddingB}[b]
      30px[d-f]
   `

   const navPanePaddingRight = `
      ${theme.tmpViewportPaddingA}[a]
      ${theme.tmpViewportPaddingB}[b]
      30px[c-f]
   `

   const contentPaneStyleProps = {
      paddingLeft: `
         ${theme.tmpViewportPaddingA}[a]
         ${theme.tmpViewportPaddingB}[b]
         30px[c-f]
      `,
      paddingRight: `
         ${theme.tmpViewportPaddingA}[a]
         ${theme.tmpViewportPaddingB}[b]
         ${theme.tmpViewportPaddingC}[c]
         ${theme.tmpViewportPaddingD}[d]
         30px[e-f]
      `
   }

   if (isBoxGL) {
      contentPaneStyleProps.marginRight = `
         ${theme.tmpViewportPaddingA}[a]
         ${theme.tmpViewportPaddingB}[b]
         ${theme.tmpViewportPaddingC}[c]
         30px[d]
         ${theme.tmpViewportPaddingE}[e]
         ${theme.tmpViewportPaddingF}[f]
      `
   }

   if (isHeaderGL) {
      navPanePaddingLeft = ''
      contentPaneStyleProps.paddingRight = `
         ${theme.tmpViewportPaddingA}[a]
         ${theme.tmpViewportPaddingB}[b]
         ${theme.tmpViewportPaddingC}[c]
         ${theme.tmpViewportPaddingD}[d]
      `

      contentPaneStyleProps.marginRight = `
         ${theme.tmpViewportPaddingE}[e]
         ${theme.tmpViewportPaddingF}[f]
      `
   }

   const buttonProps = {
      activeBackgroundColor: theme.tmpAsideButtonActiveBackgroundColor,
      activeTextColor: theme.tmpAsideButtonActiveTextColor,
      borderRadius: '0px',
      borderStyle: `1px solid ${theme.tmpAsideLineColor}`,
      paddingHorizontal: isHeaderGL
         ? '10px'
         : '10px[a-c] 30px[d-f]',
      textColor: theme.tmpAsideButtonTextColor,
      textSize: theme.tmpSecondaryButtonTextSize,
      textFontWeight: theme.tmpSecondaryButtonTextFontWeight
   }

   // TODO: This should probably go to some kind of Unauthorized component instead
   if (!initialLoading && !isLoggedIn) {
      return <NotFound />
   }

   if (initialLoading) {
      return null
   }

   return (
      <LayoutContainerFixedArea>
         <MultiPageNavPane>
            <View
               paddingLeft={navPanePaddingLeft}
               paddingRight={navPanePaddingRight}
               paddingVertical={theme.tmpSpacingTop}
               borderBottom={`1px solid ${theme.tmpAsideLineColor}`}
               width="100%">
               <Title size="md">Messages</Title>
            </View>
            <ListMenuButton
               icon={(
                  <View
                     display="flex"
                     alignItems="center"
                     style={{ opacity: '0.5' }}>
                     <InboxGlyph16Icon
                        width="14px"
                        height="14px"
                        color={theme.tmpAsideButtonTextColor}
                     />
                  </View>
               )}
               linkExact
               linkTo={`${match.url}/inbox`}
               name="Inbox"
               showBadge={myNotificationCount > 0}
               {...buttonProps}
            />
            {myDirectThreadList.items.map(thread => (
               <ListMenuButton
                  key={thread.id}
                  icon={(
                     <View
                        flex="0 0 auto"
                        display="flex"
                        flexFlow="column"
                        height="14px"
                        width="14px"
                        justifyContent="center"
                        alignItems="center"
                        transition="300ms"
                        backgroundColor="#aaa"
                        borderRadius="3px">
                        <Text
                           size="0.8"
                           weight="bold"
                           color="#fff"
                           style={{ transition: '300ms' }}>
                           {thread.title.charAt(0)}
                        </Text>
                     </View>
                  )}
                  linkTo={`${match.url}/direct/${thread.recipientUserId}/${thread.id}`}
                  name={thread.title}
                  showBadge={thread.hasUnreadEntries}
                  {...buttonProps}
               />
            ))}
            {initiativeList.items.length > 0 && (
               <View marginTop="20px">
                  <View
                     paddingVertical="10px"
                     paddingLeft={navPanePaddingLeft}
                     borderBottom={`1px solid ${theme.tmpAsideLineColor}`}>
                     <Text size="1.5" weight="medium" color="#aaa">
                        Groups
                     </Text>
                  </View>
                  {initiativeList.items.map(initiative => (
                     <ListMenuButton
                        key={initiative.id}
                        icon={(
                           <View
                              flex="0 0 auto"
                              display="flex"
                              flexFlow="column"
                              height="14px"
                              width="14px"
                              justifyContent="center"
                              alignItems="center"
                              transition="300ms"
                              backgroundColor="#aaa"
                              borderRadius="3px">
                              <Text
                                 size="0.8"
                                 weight="bold"
                                 color="#fff"
                                 style={{ transition: '300ms' }}>
                                 {initiative.name.charAt(0)}
                              </Text>
                           </View>
                        )}
                        linkTo={`${match.url}/group/${initiative.slug}/${initiative.thread.id}`}
                        name={initiative.name}
                        showBadge={initiative.thread.hasUnreadEntries}
                        {...buttonProps}
                     />
                  ))}
               </View>
            )}
         </MultiPageNavPane>
         <Switch>
            {!initialSizeIsMobile && (
               <Redirect exact from={match.url} to={`${match.url}/inbox`} />
            )}
            <Route
               path={[
                  `${match.path}/inbox`,
                  `${match.path}/direct/:threadId`,
                  `${match.path}/group/:threadId`
               ]}>
               <MultiPageContentPane>
                  <Switch>
                     <Route
                        path={`${match.path}/inbox`}
                        render={() => <Inbox {...contentPaneStyleProps} />}
                     />
                     <Route
                        path={`${match.path}/direct/:recipientUserId/:threadId`}
                        render={() => <UserThreadChat {...contentPaneStyleProps} />}
                     />
                     <Route
                        path={`${match.path}/group/:initiativeSlug/:threadId`}
                        render={() => <WrappedInitiativeThreadChat {...contentPaneStyleProps} />}
                     />
                  </Switch>
               </MultiPageContentPane>
            </Route>
         </Switch>
      </LayoutContainerFixedArea>
   )
}

export default Messages
